import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Menu,
  Bell,
  ChevronDown,
  Search,
  User,
  LogOut,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useUIStore } from '../../store/useUIStore';
import { useSocket } from '../../context/SocketContext';
import { getDisplayRole, getProfilePath } from '../../config/navigation';
import { Avatar } from '../ui/Avatar';

interface DashboardHeaderProps {
  title?: string;
  description?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
  actions?: React.ReactNode;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  title,
  description,
  breadcrumbs,
  actions,
}) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { user, isAuthenticated, isLoading, logoutUser } = useAuth();
  const { toggleMobileSidebar, isSidebarCollapsed } = useUIStore();
  const { notifications, markAllAsRead } = useSocket();

  const notificationRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const profileTriggerRef = useRef<HTMLButtonElement>(null);
  const profileActionRefs = useRef<(HTMLElement | null)[]>([]);

  const unreadNotificationsCount = notifications.filter((n) => !n.read).length;

  const handleLogout = async () => {
    setShowProfileMenu(false);
    await logoutUser();
  };

  const handleNotificationClick = () => {
    setShowProfileMenu(false);
    setShowNotifications((prev) => !prev);
    if (!showNotifications) {
      setTimeout(() => markAllAsRead(), 0);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to search results page or trigger global search
      console.log('Search:', searchQuery);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showProfileMenu) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowProfileMenu(false);
        profileTriggerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showProfileMenu]);

  useEffect(() => {
    if (!showProfileMenu) return;

    const frame = requestAnimationFrame(() => profileActionRefs.current[0]?.focus());
    return () => cancelAnimationFrame(frame);
  }, [showProfileMenu]);

  const handleProfileMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const actions = profileActionRefs.current.filter(Boolean) as HTMLElement[];
    const currentIndex = actions.indexOf(document.activeElement as HTMLElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      actions[(currentIndex + 1) % actions.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      actions[(currentIndex - 1 + actions.length) % actions.length]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      actions[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      actions[actions.length - 1]?.focus();
    }
  };

  if (isLoading || !isAuthenticated || !user) {
    return (
      <header className="sticky top-0 z-50 bg-bg-secondary border-b border-border-subtle">
        <div className="container-main">
          <div className="flex h-16 items-center justify-between">
            <button
              onClick={toggleMobileSidebar}
              className="md:hidden p-2 rounded-md text-text-secondary hover:bg-bg-tertiary"
              aria-label="Toggle sidebar"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-4">
              <div className="h-8 w-32 bg-bg-tertiary animate-pulse rounded" />
            </div>
          </div>
        </div>
      </header>
    );
  }

  const profilePath = getProfilePath(user.role, user.isAdmin);
  const effectiveRole = user.isAdmin ? 'Admin' : getDisplayRole(user.role);

  return (
    <header className="sticky top-0 z-50 bg-bg-secondary/80 backdrop-blur-sm border-b border-border-subtle">
      <div className="container-main">
        <div className="flex h-16 items-center gap-4">
          {/* Mobile Sidebar Toggle */}
          <button
            onClick={toggleMobileSidebar}
            className="md:hidden p-2 rounded-md text-text-secondary hover:bg-bg-tertiary"
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Breadcrumbs / Page Context */}
          <div className="flex-1 min-w-0 flex items-center gap-2 overflow-hidden">
            {breadcrumbs && breadcrumbs.length > 0 ? (
              <nav className="flex items-center gap-1.5 text-sm" aria-label="Breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                  <span key={crumb.href || index} className="flex items-center gap-1.5">
                    {index > 0 && <ChevronDown className="h-3.5 w-3.5 text-text-muted" />}
                    {crumb.href ? (
                      <Link
                        to={crumb.href}
                        className="text-text-secondary hover:text-text-primary transition-colors truncate"
                      >
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="text-text-primary font-medium truncate">{crumb.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            ) : title ? (
              <div className="flex flex-col min-w-0">
                <h1 className="text-heading-lg text-text-primary truncate">{title}</h1>
                {description && (
                  <p className="text-body-sm text-text-muted truncate">{description}</p>
                )}
              </div>
            ) : (
              <h1 className="text-heading-lg text-text-primary hidden sm:block">Dashboard</h1>
            )}
          </div>

          {/* Global Search */}
          <div className="relative hidden lg:block">
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-text-muted" aria-hidden="true" />
              <input
                type="search"
                placeholder="Search courses, blogs, users... (Cmd+K)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="form-control pl-10 pr-10 w-72 lg:w-96 bg-bg-tertiary border-border-subtle placeholder:text-text-muted"
                aria-label="Global search"
              />
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-caption text-text-muted bg-bg-tertiary rounded">
                <span className="kbd-key">⌘</span>K
              </kbd>
            </form>
          </div>

          {/* Header Actions */}
          <div className="flex items-center gap-2">
            {/* Custom Actions Slot */}
            {actions}

            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={handleNotificationClick}
                className="relative p-2 rounded-lg text-text-secondary hover:bg-bg-tertiary hover:text-text-primary transition-colors"
                aria-label={`Notifications${unreadNotificationsCount > 0 ? `, ${unreadNotificationsCount} unread` : ''}`}
              >
                <Bell className="h-5 w-5" />
                {unreadNotificationsCount > 0 && (
                  <span className="absolute top-1 right-1 w-4.5 h-4.5 text-[10px] font-bold text-white bg-danger rounded-full flex items-center justify-center">
                    {unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-bg-secondary border border-border-default rounded-lg shadow-xl z-50 animate-scale-in">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
                    <h4 className="text-heading-sm text-text-primary">Notifications</h4>
                    {notifications.some((n) => !n.read) && (
                      <button
                        onClick={markAllAsRead}
                        className="text-caption text-primary-600 hover:text-primary-700"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map((notif) => (
                        <div
                          key={notif.id}
                          className={`px-4 py-3 border-b border-border-subtle transition-colors ${
                            !notif.read ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                          }`}
                        >
                          <p className="text-body-sm text-text-secondary">{notif.message}</p>
                          <p className="text-caption text-text-muted mt-1">
                            {new Date(notif.timestamp).toLocaleString()}
                          </p>
                          {notif.link && (
                            <Link
                              to={notif.link}
                              onClick={() => setShowNotifications(false)}
                              className="text-caption text-primary-600 hover:text-primary-700 mt-1 inline-block"
                            >
                              View details →
                            </Link>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="px-4 py-6 text-center text-body-sm text-text-muted">No notifications yet</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative" ref={profileRef}>
              <button
                ref={profileTriggerRef}
                onClick={() => {
                  setShowNotifications(false);
                  setShowProfileMenu((prev) => !prev);
                }}
                className={`flex h-10 items-center gap-2 rounded-lg p-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
                  showProfileMenu ? 'bg-bg-tertiary' : 'hover:bg-bg-tertiary'
                }`}
                aria-label={`Account menu for ${user.name}`}
                aria-expanded={showProfileMenu}
                aria-haspopup="true"
                aria-controls="dashboard-account-menu"
              >
                <Avatar src={user.avatar} name={user.name} alt="" size="sm" />
                <span className="hidden max-w-36 truncate text-sm font-medium text-text-primary xl:block">
                  {user.name}
                </span>
                <ChevronDown className={`hidden h-4 w-4 text-text-muted transition-transform sm:block ${showProfileMenu ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>

              {showProfileMenu && (
                <div
                  id="dashboard-account-menu"
                  className="absolute right-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border-default bg-bg-secondary shadow-xl animate-scale-in"
                  role="menu"
                  aria-label="Account menu"
                  onKeyDown={handleProfileMenuKeyDown}
                >
                  <div className="flex items-center gap-3 border-b border-border-subtle px-4 py-4">
                    <Avatar src={user.avatar} name={user.name} alt="" size="md" />
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold leading-5 text-text-primary">{user.name}</p>
                      <p className="mt-0.5 truncate text-xs text-text-secondary">{user.email}</p>
                      <span className="mt-2 inline-flex rounded-md bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-700 capitalize">
                        {effectiveRole}
                      </span>
                    </div>
                  </div>
                  <div className="p-2">
                    <Link
                      ref={(element) => { profileActionRefs.current[0] = element; }}
                      to={profilePath}
                      onClick={() => setShowProfileMenu(false)}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      role="menuitem"
                    >
                      <User className="h-4.5 w-4.5 text-text-muted" aria-hidden="true" />
                      Profile
                    </Link>
                  </div>
                  <div className="mx-2 h-px bg-border-subtle" role="separator" />
                  <div className="p-2">
                    <button
                      ref={(element) => { profileActionRefs.current[1] = element; }}
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-danger transition-colors hover:bg-danger/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger"
                      role="menuitem"
                    >
                      <LogOut className="h-4.5 w-4.5" aria-hidden="true" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
